import type { SystemStatsResponse } from "@repo/types";
import { apiClient } from "./base/http";

export interface LoyaltyPointTier {
  min_spent: number;
  spend_amount: number;
  points: number;
}

export interface MemberTierRule {
  // Phase 1 — upgrade conditions
  min_spend: number;
  min_visits: number;
  upgrade_coupon_id: string | null;
  // Phase 2 — benefits
  early_booking_days: number;     // 比一般顧客提早幾天可預約（延伸可預約的最遠日期）
  early_calendar_days: number;    // 比一般顧客提早幾天看到店家「每月N日開放下個月」的行事曆
  discount_pct: number;           // 預約折扣 %（0 = 不折扣）
  // Phase 3 — monthly reward & downgrade
  monthly_coupon_id: string | null;   // 每月自動發放優惠券
  downgrade_period_days: number;      // 考核區間（天），0 = 不降等
  downgrade_min_spend: number;        // 區間內最低消費
  downgrade_min_visits: number;       // 區間內最低次數
}

export interface MemberTierConfig {
  enabled: boolean;
  vip: MemberTierRule;
  vvip: MemberTierRule;
}

export type DeviceBookingMode = "follow_staff" | "staff_and_device";

export const DEVICE_BOOKING_MODE_LABELS: Record<DeviceBookingMode, string> = {
  follow_staff: "系統自動分配設備",
  staff_and_device: "客人自選設備",
};

export function nextDeviceBookingMode(current?: DeviceBookingMode | null): DeviceBookingMode {
  return current === "staff_and_device" ? "follow_staff" : "staff_and_device";
}

export interface SystemSettings {
  businessName?: string;
  businessPhone?: string;
  businessEmail?: string;
  businessAddress?: string;
  businessHours?: string;
  businessLogoUrl?: string;
  announcement?: string;
  enableAssistants?: boolean;
  assistantFeePercentage?: number;
  assistantFeeFixed?: number;
  appointmentReminderDays?: number;
  enableOnlineBooking?: boolean;
  bookingAdvanceDays?: number;
  enablePaymentCash?: boolean;
  enablePaymentLinepay?: boolean;
  enablePaymentBankTransfer?: boolean;
  bankTransferCode?: string;
  bankTransferAccount?: string;
  enabledPaymentMethods?: string[];
  autoCancelDepositHours?: number;
  depositReminderHoursBeforeCancel?: number;
  groupPaymentMode?: "group" | "per_booking";
  allowMultiPersonBooking?: boolean;
  maxPartySize?: number | null;
  depositEnabled?: boolean;
  depositType?: "fixed" | "percentage";
  depositAmount?: number;
  noShowDepositAllocation?: "commission" | "employee";
  allowFullPayment?: boolean;
  loyaltyPointsEnabled?: boolean;
  loyaltyPointsTiers?: LoyaltyPointTier[];
  loyaltyPointsRedemptionRate?: number;
  /** 點數到期機制二選一：months = 單筆各自到期；annual_reset = 每年固定日期整戶歸零。未設定 = months（維持舊行為） */
  loyaltyPointsExpiryMode?: "months" | "annual_reset";
  /** 點數有效期限（月），0 或未設定 = 永久不過期，只在 loyaltyPointsExpiryMode 為 months 時生效 */
  loyaltyPointsExpiryMonths?: number;
  /** 年度歸零日期，格式 "MM-DD"（例如 "12-31"），只在 loyaltyPointsExpiryMode 為 annual_reset 時生效 */
  loyaltyPointsAnnualResetDate?: string;
  requireSignature?: boolean;
  extraSlotEnabled?: boolean;
  extraSlotLabel?: string;
  enableProductCommission?: boolean;
  /** 服務抽成：啟用時服務可個別設定固定抽成比例，該服務營收獨立以此比例計算抽成，不併入員工階梯抽成的營收基礎（比照商品抽成的處理方式） */
  enableServiceCommission?: boolean;
  /** 不指定業績抽成：啟用時「指定」與「不指定」業績分開計算抽成階梯；停用時全部併入指定抽成一起算。未設定 = 啟用（維持舊行為） */
  enableRandomCommission?: boolean;
  /**
   * 折扣是否由員工承擔。未設定 = true（維持舊行為）。
   * true（預設，現況）：員工承擔折扣 — 預約層級折扣 / 部分付款按比例分攤，壓低員工的服務・商品・總業績與抽成營收。
   * false：員工不承擔 — 服務、商品、總業績、抽成營收一律以「原價」計算，預約折扣不影響員工業績。
   */
  staffBearsDiscount?: boolean;
  allowPackageTransfer?: boolean;
  /** 預約相關 iOS 推播（新預約 / 定金 / 取消）只通知老闆與被預約員工；false/未設定 = 老闆名下所有員工都收到 */
  bookingPushOwnerAndStaffOnly?: boolean;
  /**
   * 設備預約模式。未設定 = follow_staff（維持舊行為）。
   * follow_staff：官網只選人員，設備由後端依人員/服務自動分配（官網不顯示設備）。
   * staff_and_device：官網選人員之外再選設備（可「不指定」自動分配）。
   */
  deviceBookingMode?: DeviceBookingMode;
  /** 開放設備租借：開啟後，標記為「設備租借」的服務會出現在官網，客人選設備與時段即可預約（不需人員）。未設定 = false */
  deviceRentalEnabled?: boolean;
  /** 設備租借需審核顧客：開啟後只有在顧客管理被核准（customers.device_rental_approved）的顧客，登入前台才看得到/能送出設備租借。未設定 = false */
  deviceRentalRequireApproval?: boolean;
  /**
   * 設備沒空不開放預約。未設定 = false（維持舊行為：分配不到設備預約照樣成立）。
   * 開啟後官網預約：服務有綁設備時，時段要至少有一台可用（含包廂「只接同一組」的鎖定）才顯示，送出時分配不到就不成立。
   */
  deviceStrictAvailability?: boolean;
  /** 設備配置是否啟用「平面圖」檢視。未設定 = true；關閉後只剩列表與時間軸 */
  deviceFloorEnabled?: boolean;
  /** 平面圖場地大小（畫布邏輯像素）。未設定 = 1000 × 640 */
  deviceFloorWidth?: number;
  deviceFloorHeight?: number;
  enableMultiLocale?: boolean;
  enabledLocales?: string[];
  customerSelfCancelHours?: number;
  lineCustomReminderEnabled?: boolean;
  lineCustomReminderText?: string;
  memberTierConfig?: MemberTierConfig;
}

export interface SystemSettingsResponse {
  success: boolean;
  settings: SystemSettings;
}

export const fetchSystemStats = async () => {
  const { data } = await apiClient.get<SystemStatsResponse>("/api/system/stats");
  if (!data.success) {
    throw new Error("無法取得系統統計資料");
  }
  return data.stats;
};

export const fetchSystemSettings = async (): Promise<SystemSettings> => {
  const { data } = await apiClient.get<SystemSettingsResponse>("/api/system/settings");
  if (!data.success) {
    throw new Error("無法取得系統設定");
  }
  return data.settings;
};

export const updateSystemSettings = async (settings: Partial<SystemSettings>): Promise<void> => {
  const { data } = await apiClient.put<{ success: boolean; message: string }>("/api/system/settings", settings);
  if (!data.success) {
    throw new Error(data.message || "更新系統設定失敗");
  }
};

export const updateSystemSettingsAtomic = async (settings: Partial<SystemSettings>): Promise<void> => {
  const { data } = await apiClient.put<{ success: boolean; message: string }>("/api/system/settings/atomic", settings);
  if (!data.success) {
    throw new Error(data.message || "Failed to update settings");
  }
};

export interface PlatformPaymentMethod {
  id: string;
  code: string;
  label: string;
  description: string;
  sort_order: number;
  requires_config: boolean;
  config_label: string;
  is_active?: boolean;
}

// ─── Dashboard Layout persistence ───

export const fetchDashboardLayout = async (): Promise<Record<string, unknown> | null> => {
  const { data } = await apiClient.get<SystemSettingsResponse>("/api/system/settings");
  if (!data.success) return null;
  const raw = (data.settings as Record<string, unknown>).dashboardLayout;
  if (raw && typeof raw === "object") return raw as Record<string, unknown>;
  return null;
};

export const updateDashboardLayout = async (layout: Record<string, unknown>): Promise<void> => {
  const { data } = await apiClient.put<{ success: boolean; message: string }>("/api/system/settings", {
    dashboardLayout: layout
  });
  if (!data.success) {
    throw new Error(data.message || "儀表板佈局儲存失敗");
  }
};

export const fetchAvailablePaymentMethods = async (): Promise<PlatformPaymentMethod[]> => {
  const { data } = await apiClient.get<{ success: boolean; methods: PlatformPaymentMethod[] }>("/api/system/payment-methods");
  return data.methods;
};
